import React, {useContext, useRef} from 'react';

import {Button, Dropdown, IconButton, Text, WithToggle} from '@albert-io/atomic';
import {SortOption} from 'client/Reports/reports.types';
import {ReportsContext} from 'client/Reports/Reports.context';
import {METRIC_COLUMNS} from 'client/Reports/reports.utils';

interface Props {
  onSortClick: (sortKey: string, sortDirection: SortOption) => void;
  sortDirection: SortOption;
  sortBy: string | null;
}

export const AnswerBreakdownHeaderCell = ({sortDirection, onSortClick, sortBy}: Props) => {
  const {variables, dimensions} = useContext(ReportsContext);
  const isSingleStudentQuestion =
    (dimensions.includes('students') && variables.questionFilter) ||
    (dimensions.includes('questions') && variables.studentFilter);

  const SORTS = [
    {
      label: 'Correct',
      sortKey: 'guess_metrics.count_correct_attempts'
    },
    {
      label: 'Incorrect',
      sortKey: 'guess_metrics.count_incorrect_attempts'
    },
    ...(isSingleStudentQuestion
      ? []
      : [
          {
            label: 'Total',
            sortKey: 'guess_metrics.count_attempts'
          }
        ]),
    {
      label: 'Unanswered',
      sortKey: 'question_metrics.count_unattempted'
    }
  ] as const;

  const toggleRef = useRef(null);

  const isCurrentSort =
    sortBy &&
    [
      'guess_metrics.count_correct_attempts',
      'guess_metrics.count_incorrect_attempts',
      'guess_metrics.count_attempts',
      'question_metrics.count_unattempted'
    ].includes(sortBy);

  const columnDefinition = METRIC_COLUMNS.find((c) => c.key === 'answer_breakdown')!;
  // should never happen, just a way to get ts to play nice
  if (columnDefinition.metricType !== 'renderedTitle') {
    return null;
  }

  return (
    <th className='reports-table__header'>
      <div className='u-display_flex u-justify-content_space-between u-align-items_center'>
        <columnDefinition.renderTitle dimensions={dimensions} variables={variables} />
        <WithToggle>
          {({on, onClick}) => (
            <>
              <IconButton
                color={isCurrentSort ? 'primary' : 'secondary'}
                className='o-table__header-sort'
                label='sort'
                iconStyle='regular'
                size='s'
                icon={isCurrentSort && sortDirection === 'desc' ? 'arrow-down' : 'arrow-up'}
                onClick={onClick}
                ref={toggleRef}
              />
              {on ? (
                <Dropdown.Tray
                  className='u-border-radius_2 u-box-shadow_default u-bgc_white u-display_flex u-flex-direction_column u-border u-border-color_slate-300 u-overflow_hidden u-bgc_white'
                  positionFixed
                  placement='bottom-end'
                  target={toggleRef.current}
                  style={{zIndex: 11}}
                >
                  <div
                    style={{fontSize: 12}}
                    className='u-bgc_white u-white-space_nowrap u-width_100pc u-display_inline-flex u-justify-content_space-between u-align-items_center u-border-b u-border-color_slate-200 u-border-radius_0 u-clr_slate-500 u-pad-tb_1 u-pad-lr_2'
                  >
                    Sort by...
                  </div>
                  {SORTS.map(({label, sortKey}) => (
                    <Button
                      className='u-bgc_white u-white-space_nowrap u-width_100pc u-display_inline-flex u-justify-content_space-between u-align-items_center u-border-b u-border-color_slate-200 u-border-radius_0'
                      color='secondary'
                      key={sortKey}
                      variant='text'
                      onClick={() => onSortClick(sortKey, 'desc')}
                    >
                      <Text color={sortBy === sortKey ? 'blue' : 'black'}>{label}</Text>
                    </Button>
                  ))}
                </Dropdown.Tray>
              ) : null}
            </>
          )}
        </WithToggle>
      </div>
    </th>
  );
};
